import { useState, useCallback, useRef } from 'react'
import { AutoComplete } from 'antd'
import { setOptions } from '@googlemaps/js-api-loader'

export interface ParsedAddress {
  indirizzo: string
  numero_civico?: string
  citta: string
  provincia: string
  cap: string
}

interface AddressOption {
  value: string   // placeId — unique key
  label: string   // displayed in dropdown
  parsed: ParsedAddress
}

interface AddressAutocompleteInputProps {
  value?: string
  onChange?: (value: string) => void
  onPlaceSelect?: (parsed: ParsedAddress) => void
  placeholder?: string
  size?: 'small' | 'middle' | 'large'
  disabled?: boolean
}

let mapsReady = false

async function ensureMapsLoaded(): Promise<void> {
  if (mapsReady) return
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  if (!apiKey) throw new Error('VITE_GOOGLE_MAPS_API_KEY non configurata')
  setOptions({ key: apiKey, v: 'weekly' })
  await (window as any).google.maps.importLibrary('places')
  mapsReady = true
}

async function getPlacesLib(): Promise<any> {
  await ensureMapsLoaded()
  return await (window as any).google.maps.importLibrary('places')
}

function parseCityBlock(text: string): { citta: string; provincia: string; cap: string } {
  const clean = text.replace(/,?\s*(Italia|Italy)$/i, '').trim()
  const m1 = clean.match(/^(\d{5}\s+)?(.+?)\s+([A-Z]{2})$/i)
  if (m1) return { cap: m1[1]?.trim() ?? '', citta: m1[2].trim(), provincia: m1[3].toUpperCase() }
  const m2 = clean.match(/^(.+?),\s*([A-Z]{2})$/i)
  if (m2) return { cap: '', citta: m2[1].trim(), provincia: m2[2].toUpperCase() }
  return { citta: clean, provincia: '', cap: '' }
}

function parseFullText(fullText: string): ParsedAddress {
  const withoutCountry = fullText.replace(/,?\s*(Italia|Italy)$/i, '').trim()
  const parts = withoutCountry.split(',').map(p => p.trim()).filter(Boolean)
  if (parts.length === 0) return { indirizzo: fullText, citta: '', provincia: '', cap: '' }

  const provIdx = parts.findIndex(p => /^[A-Z]{2}$/.test(p))
  if (provIdx !== -1) {
    const provincia = parts[provIdx]
    const citta = provIdx > 0 ? parts[provIdx - 1] : ''
    let cap = ''
    let cityStartIdx = provIdx - 1
    if (provIdx >= 2 && /^\d{5}$/.test(parts[provIdx - 2])) {
      cap = parts[provIdx - 2]
      cityStartIdx = provIdx - 2
    }
    const indirizzo = parts.slice(0, cityStartIdx).join(', ')
    return { indirizzo: indirizzo || parts[0], citta, provincia, cap }
  }

  const lastPart = parts[parts.length - 1]
  const { citta, provincia, cap } = parseCityBlock(lastPart)
  const indirizzo = parts.slice(0, -1).join(', ')
  return { indirizzo: indirizzo || parts[0], citta, provincia, cap }
}

function getComponent(components: any[], type: string, short = false): string {
  const c = components.find((c: any) => Array.isArray(c.types) && c.types.includes(type))
  if (!c) return ''
  return short ? (c.shortText ?? c.short_name ?? '') : (c.longText ?? c.long_name ?? '')
}

function buildLabel(parsed: ParsedAddress): string {
  const parts: string[] = [parsed.indirizzo]
  if (parsed.numero_civico) parts.push(parsed.numero_civico)
  if (parsed.cap) parts.push(parsed.cap)
  if (parsed.citta) parts.push(parsed.citta)
  if (parsed.provincia) parts.push(`(${parsed.provincia})`)
  return parts.join(', ')
}

async function enrichOption(places: any, opt: AddressOption): Promise<AddressOption> {
  try {
    const place = new places.Place({ id: opt.value })
    await place.fetchFields({ fields: ['addressComponents'] })
    const comps = place.addressComponents
    if (!comps || comps.length === 0) return opt

    const indirizzo = getComponent(comps, 'route') || opt.parsed.indirizzo
    const numero_civico = getComponent(comps, 'street_number') || undefined
    const citta =
      getComponent(comps, 'locality') ||
      getComponent(comps, 'administrative_area_level_3') ||
      opt.parsed.citta
    const provincia = getComponent(comps, 'administrative_area_level_2', true) || opt.parsed.provincia
    const cap = getComponent(comps, 'postal_code') || opt.parsed.cap

    const parsed: ParsedAddress = { indirizzo, numero_civico, citta, provincia, cap }
    return { ...opt, label: buildLabel(parsed), parsed }
  } catch {
    return opt
  }
}

const AddressAutocompleteInput = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Inizia a scrivere l'indirizzo...",
  size = 'small',
  disabled = false,
}: AddressAutocompleteInputProps) => {
  const [options, setOptions] = useState<AddressOption[]>([])
  const optionsRef = useRef<AddressOption[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sessionTokenRef = useRef<any>(null)
  const reqIdRef = useRef(0)

  const fetchSuggestions = useCallback(async (input: string) => {
    if (input.length < 3) { setOptions([]); return }
    const myId = ++reqIdRef.current
    try {
      const places = await getPlacesLib()
      if (!places?.AutocompleteSuggestion) return

      if (!sessionTokenRef.current && places.AutocompleteSessionToken) {
        sessionTokenRef.current = new places.AutocompleteSessionToken()
      }

      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['it'],
        sessionToken: sessionTokenRef.current,
      })

      if (myId !== reqIdRef.current) return

      // Phase 1: show suggestions immediately from text parsing
      const optsBase: AddressOption[] = suggestions.map((s: any, idx: number) => {
        const pred = s.placePrediction
        const placeId: string = pred.placeId ?? `${myId}-${idx}`
        const fullText: string = pred.text?.text ?? ''
        const mainText: string | undefined = pred.structuredFormat?.mainText?.text
        const secondaryText: string | undefined = pred.structuredFormat?.secondaryText?.text

        let parsed: ParsedAddress
        if (mainText && secondaryText !== undefined) {
          const { citta, provincia, cap } = parseCityBlock(secondaryText)
          parsed = { indirizzo: mainText, citta, provincia, cap }
        } else {
          parsed = parseFullText(fullText)
        }

        return { value: placeId, label: fullText, parsed }
      })

      setOptions(optsBase)
      optionsRef.current = optsBase

      // Phase 2: enrich each suggestion with full address details (CAP, civico)
      if (!places.Place) return
      const enriched = await Promise.all(optsBase.map(opt => enrichOption(places, opt)))

      if (myId !== reqIdRef.current) return
      setOptions(enriched)
      optionsRef.current = enriched
    } catch (e) {
      console.error('Places autocomplete error:', e)
    }
  }, [])

  const handleSearch = (text: string) => {
    // Ant Design fires onSearch before onSelect — skip if text is a placeId
    if (optionsRef.current.some(o => o.value === text)) return
    onChange?.(text)
    setOptions([])
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (text.length >= 3) {
      debounceRef.current = setTimeout(() => fetchSuggestions(text), 300)
    }
  }

  const handleSelect = (selectedValue: string) => {
    const opt = optionsRef.current.find(o => o.value === selectedValue)
    setOptions([])
    optionsRef.current = []
    sessionTokenRef.current = null

    if (opt) {
      onChange?.(opt.parsed.indirizzo)
      onPlaceSelect?.(opt.parsed)
    }
  }

  return (
    <AutoComplete
      value={value}
      options={options.map(o => ({ value: o.value, label: o.label }))}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholder}
      size={size}
      disabled={disabled}
      style={{ width: '100%' }}
      onFocus={() => getPlacesLib().catch(() => {})}
      filterOption={false}
      backfill={false}
    />
  )
}

export default AddressAutocompleteInput
